import { type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import './ConfirmDialog.css';

interface ConfirmDialogProps {
  isOpen: boolean;
  /** Body text or richer content. No title — this is the plain two-button ask. */
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Optional THIRD choice, between cancel and confirm. For asks that are not
   *  yes/no — "unsaved changes" is save / discard / keep editing, where Escape
   *  must mean keep editing, not discard. */
  altLabel?: string;
  onAlt?: () => void;
  /** 'danger' tints the confirm button red for destructive actions. */
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  /** Disables the confirm button (e.g. while the action is in flight). */
  busy?: boolean;
}

/**
 * Standard OK/Cancel confirmation. No title bar — just a message and two
 * buttons, both relabelable — replacing native `window.confirm`, which cannot be
 * styled and blocks the event loop. Built on the shared Modal (portal, overlay,
 * Escape-to-cancel). Pass `altLabel`/`onAlt` for a third choice; cancel stays
 * the Escape/overlay outcome, so it must always be the safe one.
 */
export function ConfirmDialog({
  isOpen,
  message,
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  altLabel,
  onAlt,
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  busy = false,
}: ConfirmDialogProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      size="sm"
      closeOnOverlayClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="confirm-dialog-actions">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          {altLabel && onAlt && (
            <Button variant="outline" onClick={onAlt} disabled={busy}>
              {altLabel}
            </Button>
          )}
          <Button variant={confirmVariant} onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="confirm-dialog-message">{message}</div>
    </Modal>
  );
}
